import { useEffect, useRef, useState, useCallback } from "react";
import { fmt } from "../utils/fmt";
import { DEBRIS_ITEMS, BASE_MATERIALS } from "../data/items";
import { SMELT_RECIPES } from "../data/recipes";

// ── Game modules ──────────────────────────────────────────────────────────────
// Enemy logic (spawning, AI, bullets, draw) is fully isolated in enemyLogic.js
import {
  WRECK_LIFE, WRECK_DRIFT,
  initEnemies, updateEnemies, updateEnemyBullets,
  drawEnemies, drawEnemyBullets,
} from "../game/enemyLogic";
import { updateTelegraphs, updateQtes, resolveQte, drawTelegraphs } from "../game/combat";
import { CombatOverlay, TACTICAL_PRESETS } from "./CombatOverlay";

// Player ship logic (movement, blaster, shield, draw) lives in playerShip.js
import {
  SHIELD_RECHARGE_DELAY,
  aLerp,
  updatePlayerMovement, updateAutoNav,
  updateBlasterTarget, updateBlasterFire,
  updatePlayerBullets, updateShieldRecharge,
  drawEngineTrail, drawPlayerShip, drawPlayerBullets,
} from "../game/playerShip";

// ── HarvestTab-local constants ────────────────────────────────────────────────
const DRONE_RANGE   = 85;
const TRACTOR_PULL  = 0.9;  // pull strength per frame at range edge (stronger closer)
const TRACTOR_BASE  = 15;   // minimum tractor range even with 0 attractRange upgrades

// ── Per-node-type background themes ──────────────────────────────────────────
// Each theme defines the radial gradient stops (inner → mid → outer),
// the star tint color, and up to three nebula blobs with position/color/radius.
const NODE_THEMES = {
  // Deep blue — peaceful mining field
  extraction: {
    bg:   ["#0a1422", "#060c18", "#020408"],
    star: "160,195,255",
    nebulae: [
      { ox: 0.25, oy: 0.30, r: 0.45, rgb: "20,60,120",  a: 0.07 },
      { ox: 0.75, oy: 0.70, r: 0.35, rgb: "10,30,80",   a: 0.05 },
    ],
  },
  // Purple — rare and mysterious
  material_site: {
    bg:   ["#110a22", "#0a0618", "#040208"],
    star: "200,160,255",
    nebulae: [
      { ox: 0.40, oy: 0.35, r: 0.50, rgb: "80,20,160",  a: 0.09 },
      { ox: 0.70, oy: 0.65, r: 0.38, rgb: "40,10,100",  a: 0.06 },
    ],
  },
  // Red — hostile, dangerous
  combat_zone: {
    bg:   ["#1a0808", "#100404", "#060102"],
    star: "255,160,140",
    nebulae: [
      { ox: 0.35, oy: 0.40, r: 0.48, rgb: "140,20,20",  a: 0.10 },
      { ox: 0.65, oy: 0.60, r: 0.36, rgb: "100,10,10",  a: 0.07 },
    ],
  },
  // Amber/gold — industrial refueling station
  fuel_depot: {
    bg:   ["#141006", "#0c0a04", "#040302"],
    star: "255,220,140",
    nebulae: [
      { ox: 0.30, oy: 0.55, r: 0.42, rgb: "120,80,10",  a: 0.08 },
      { ox: 0.68, oy: 0.35, r: 0.32, rgb: "80,50,5",    a: 0.05 },
    ],
  },
  // Teal — civilised trade hub, warmer feel
  trade_outpost: {
    bg:   ["#061814", "#04100e", "#020806"],
    star: "140,255,220",
    nebulae: [
      { ox: 0.45, oy: 0.40, r: 0.46, rgb: "10,100,80",  a: 0.09 },
      { ox: 0.65, oy: 0.65, r: 0.34, rgb: "5,60,50",    a: 0.06 },
    ],
  },
  // Bright green — sector entry/exit gate
  gate_in: {
    bg:   ["#061408", "#040c06", "#020402"],
    star: "160,255,180",
    nebulae: [
      { ox: 0.50, oy: 0.45, r: 0.50, rgb: "20,100,40",  a: 0.08 },
    ],
  },
  gate_out: {
    bg:   ["#141006", "#0c0904", "#040302"],
    star: "255,230,140",
    nebulae: [
      { ox: 0.50, oy: 0.45, r: 0.50, rgb: "110,80,10",  a: 0.09 },
    ],
  },
  // Fallback — plain deep space
  default: {
    bg:   ["#0a0f1a", "#060c14", "#020408"],
    star: "160,180,220",
    nebulae: [
      { ox: 0.35, oy: 0.40, r: 0.40, rgb: "20,40,90",   a: 0.05 },
    ],
  },
};

export function HarvestTab({
  nodeActivePerks,
  statsRef, perksRef, autoNavRef, droneCountRef, devSpawnRateRef,
  setInventory, setMaterials,
  canvasRef, tutorialRef, onShipMoved, onDebrisDestroyed, onItemDrop, onEnemyKill,
  currentNode, nodeType, nodeMat, nodeDropPool, nodeRefinedPool, isEnemyNode, onPlayerDeath, sectorIdxRef,
  addXp, onLevelUpRef, nodeEchoMultRef,
  setVoidCreds, onGuardianDown,
  autoNav, setAutoNav, droneCount, setDroneCount, stats,
  discoveredMaterials,
}) {
  const onGuardianDownRef = useRef(onGuardianDown);
  useEffect(() => { onGuardianDownRef.current = onGuardianDown; }, [onGuardianDown]);
  const nodeMatRef              = useRef(nodeMat);
  const nodeDropPoolRef         = useRef(nodeDropPool);
  const nodeRefinedPoolRef      = useRef(nodeRefinedPool);
  const discoveredMaterialsRef  = useRef(discoveredMaterials);
  useEffect(() => { nodeMatRef.current            = nodeMat;            }, [nodeMat]);
  useEffect(() => { nodeDropPoolRef.current       = nodeDropPool;       }, [nodeDropPool]);
  useEffect(() => { nodeRefinedPoolRef.current    = nodeRefinedPool;    }, [nodeRefinedPool]);
  useEffect(() => { discoveredMaterialsRef.current = discoveredMaterials; }, [discoveredMaterials]);
  // ── Tactical preset (combat.md §1) ─────────────────────────────────────────
  const [tacticalPreset, setTacticalPreset] = useState("balanced");
  const presetRef = useRef(tacticalPreset);
  useEffect(() => { presetRef.current = tacticalPreset; }, [tacticalPreset]);

  // Core heat, perfect streak (both are refs — canvas loop owns them)
  const coreHeatRef   = useRef(0);
  const streakRef     = useRef(0);
  const stRefExt      = useRef(null); // exposes `st` to CombatOverlay

  // Is this node a boss node? (gate_out becomes a combat encounter with Guardian)
  const isGuardianNode = nodeType === "gate_out";

  // Resolve a QTE. success=true → reward; success=false → apply penalty (hp dmg).
  const handleResolveQte = useCallback((qteId, success) => {
    const st = stRefExt.current;
    if (!st) return;
    resolveQte(st, qteId, success, {
      onHit: (dmg) => {
        if (isDeadRef.current || !dmg) return;
        let dmgLeft = dmg;
        if (st.shieldHp > 0) {
          const absorbed = Math.min(st.shieldHp, dmgLeft);
          st.shieldHp = Math.max(0, st.shieldHp - absorbed);
          dmgLeft    -= absorbed;
        }
        const newHp = Math.max(0, playerHpRef.current - dmgLeft);
        playerHpRef.current = newHp;
        setPlayerHp(newHp);
        setPlayerShield(st.shieldHp);
        if (newHp <= 0 && !isDeadRef.current) {
          isDeadRef.current = true;
          setIsDead(true);
          if (onPlayerDeathRef.current) onPlayerDeathRef.current();
        }
      },
      onStripArmor: () => { /* visual flash handled in drawEnemies */ },
      onClear:      () => { /* successful QTE — handled inside resolveQte */ },
      onTether:     () => { /* tether remains until slider is beaten */ },
    });
    if (!success) st.combatMisses = (st.combatMisses ?? 0) + 1;
  }, []);
  // Keep ref copies of callbacks so the canvas loop never captures stale closures
  const onItemDropRef    = useRef(onItemDrop);
  const onEnemyKillRef   = useRef(onEnemyKill);
  const onPlayerDeathRef = useRef(onPlayerDeath);
  const isEnemyNodeRef   = useRef(isEnemyNode);
  const currentNodeRef   = useRef(currentNode);
  const nodeTypeRef      = useRef(nodeType ?? "default");
  const addXpRef         = useRef(addXp);
  useEffect(() => { onItemDropRef.current    = onItemDrop;    }, [onItemDrop]);
  useEffect(() => { onEnemyKillRef.current   = onEnemyKill;   }, [onEnemyKill]);
  useEffect(() => { onPlayerDeathRef.current = onPlayerDeath; }, [onPlayerDeath]);
  useEffect(() => { isEnemyNodeRef.current   = isEnemyNode;   }, [isEnemyNode]);
  useEffect(() => { currentNodeRef.current   = currentNode;   }, [currentNode]);
  useEffect(() => { nodeTypeRef.current      = nodeType ?? "default"; }, [nodeType]);
  useEffect(() => { addXpRef.current         = addXp;         }, [addXp]);

  // ── Player HP / shield — React state drives the HUD, refs drive the loop ──
  const [playerHp,      setPlayerHp]      = useState(100);
  const [playerMaxHp,   setPlayerMaxHp]   = useState(100);
  const [playerShield,  setPlayerShield]  = useState(0);
  const [shieldMax,     setShieldMax]     = useState(0);
  const [isDead,        setIsDead]        = useState(false);
  const [enemiesLeft,   setEnemiesLeft]   = useState(0);
  const [showClearBanner, setShowClearBanner] = useState(false);
  const clearBannerTimerRef = useRef(null);

  // Refs for canvas loop (avoid triggering re-renders)
  const playerHpRef     = useRef(100);
  const playerMaxHpRef  = useRef(100);
  const playerShieldRef = useRef(0);
  const shieldMaxRef    = useRef(0);
  const isDeadRef       = useRef(false);

  // Sync both ref and React state in one call (used by updateEnemyBullets)
  const setHp = useCallback((hp, shield) => {
    playerHpRef.current     = hp;
    playerShieldRef.current = shield;
    setPlayerHp(hp);
    setPlayerShield(shield);
  }, []);

  // Register level-up heal callback so void.jsx can trigger heals on level-up
  useEffect(() => {
    if (!onLevelUpRef) return;
    onLevelUpRef.current = (healAmt) => {
      if (isDeadRef.current) return;
      const maxHp = playerMaxHpRef.current;
      const newHp = Math.min(maxHp, playerHpRef.current + healAmt);
      playerHpRef.current = newHp;
      setPlayerHp(newHp);
    };
    return () => { if (onLevelUpRef) onLevelUpRef.current = null; };
  }, [onLevelUpRef]);

  // Reset HP/shield whenever the player moves to a new node
  useEffect(() => {
    const s      = statsRef.current;
    const maxHp  = s.playerMaxHp ?? 100;
    const maxShd = s.shieldMax   ?? 0;
    playerMaxHpRef.current  = maxHp;
    playerHpRef.current     = maxHp;
    playerShieldRef.current = maxShd;
    shieldMaxRef.current    = maxShd;
    isDeadRef.current       = false;
    setPlayerHp(maxHp);
    setPlayerMaxHp(maxHp);
    setPlayerShield(maxShd);
    setShieldMax(maxShd);
    setIsDead(false);
    setShowClearBanner(false);
    if (clearBannerTimerRef.current) clearTimeout(clearBannerTimerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentNode]);

  // ── Canvas loop — runs once on mount, never restarts ──────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId, W, H, cx, cy;
    let shipInited = false;

    // shipStartRef is passed to updatePlayerMovement for distance-tracking
    const shipStartRef = { current: null };

    // ── Canvas resize ───────────────────────────────────────────────────────
    function resize() {
      const dpr    = window.devicePixelRatio || 1;
      const parent = canvas.parentElement;
      const rect   = parent
        ? parent.getBoundingClientRect()
        : { width: window.innerWidth, height: window.innerHeight };
      const newW = rect.width  > 10 ? rect.width  : Math.min(window.innerWidth,  430);
      const newH = rect.height > 10 ? rect.height : Math.min(window.innerHeight, 932) - 50;
      if (newW === W && newH === H) return;
      W = newW; H = newH;
      canvas.width  = W * dpr; canvas.height = H * dpr;
      canvas.style.width  = W + "px"; canvas.style.height = H + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      cx = W / 2; cy = H / 2;
      shipInited = false; // re-center ship after resize
    }
    resize();
    setTimeout(resize, 50);
    setTimeout(resize, 200);
    window.addEventListener("resize", resize);

    // ── Shared game-state object ─────────────────────────────────────────────
    // Mutated in-place every frame — React state is only updated for HUD values.
    const st = {
      shipX: 0, shipY: 0,
      laserAngle: 0, targetAngle: 0,
      moveTargetX: null, moveTargetY: null, pointerDown: false,
      debris: [],        // active asteroid/debris objects
      wrecks: [],        // destroyed debris waiting for tractor collection
      enemies: [],       // enemy ships (populated by initEnemies on enemy nodes)
      enemyBullets: [],  // red enemy projectiles
      playerBullets: [], // blue blaster bolts
      blasterTimer: 0,   // frames until next player shot
      shieldHp: 0,       // current shield HP (mirrors shieldMaxRef)
      shieldHitTimer: 0, // countdown after last hit; recharge starts at 0
      telegraphs: [],    // enemy attack warning zones (combat.md §2)
      qtes: [],          // active QTE minigames (combat.md §2)
      coreHeat: 0,       // current blaster core heat (0 → coreHeatMax)
      coreHeatLocked: false, // true while overheated — blaster disabled
      combatMisses: 0,   // # of failed QTEs/hits this node (0 = perfect)
      particles: [], floats: [], pulse: 0, spawnT: 0,
      stars: [], engineTrail: [], drones: [],
      iFrames: 0,
    };
    stRefExt.current = st;
    // Pre-populate background stars (static positions, random brightness)
    for (let i = 0; i < 100; i++) {
      st.stars.push({ x: Math.random(), y: Math.random(), s: Math.random() * 1.3 + 0.3, b: Math.random() });
    }

    // ── Debris spawner ────────────────────────────────────────────────────────
    function spawn() {
      const ang  = Math.random() * Math.PI * 2;
      const maxR = Math.min(W, H) * 0.42;
      const dist = maxR * 0.3 + Math.random() * maxR * 0.7;
      const sz   = 12 + Math.random() * 20;
      const hp   = sz * 3;
      const sp   = 0.02 + Math.random() * 0.06;
      const oa   = ang + (Math.random() - 0.5) * 1.5; // slight drift angle offset
      const pks  = perksRef.current;

      // Build drop list from this node's seeded pool.
      // material_site: guaranteed discoverable mat + 1 random from pool
      // others: 1-2 picks from the node's dropPool
      const pool     = nodeDropPoolRef.current ?? [];
      const isMatSite = nodeTypeRef.current === "material_site";
      const drops = [];

      // Discoverable mat guaranteed at material_site
      if (isMatSite && nodeMatRef.current) {
        const mat = DEBRIS_ITEMS.find(d => d.id === nodeMatRef.current);
        if (mat) drops.push({ ...mat, qty: 1 + Math.floor(Math.random() * 2) });
      }

      // 1-2 raw item drops from the node's pool
      const rawCount = isMatSite ? 1 : 1 + Math.floor(Math.random() * 2);
      for (let d = 0; d < rawCount; d++) {
        const rawPool = pool.length > 0 ? pool : ["copper", "glass_shard", "hull_debris"];
        const pickId  = rawPool[Math.floor(Math.random() * rawPool.length)];
        const meta    = DEBRIS_ITEMS.find(di => di.id === pickId);
        if (!meta) continue;
        let qty = 1 + Math.floor(Math.random() * 2);
        for (const pk of pks) {
          if (pk.id === "copper_boost"  && pickId === "copper")       qty += Math.ceil(qty * 0.5);
          if (pk.id === "glass_boost"   && pickId === "glass_shard")  qty += Math.ceil(qty * 0.5);
          if (pk.id === "hull_boost"    && pickId === "hull_debris")  qty += Math.ceil(qty * 0.5);
          if (pk.id === "isotope_boost" && pickId === "isotope8")     qty += Math.ceil(qty * 0.5);
          if (pk.id === "silica_boost"  && pickId === "silica_dust")  qty += Math.ceil(qty * 0.5);
          if (pk.id === "panel_boost"   && pickId === "panel")        qty += Math.ceil(qty * 0.5);
        }
        drops.push({ ...meta, qty });
      }

      // ~8% chance of a T1 refined drop from the node's seeded pool
      if (Math.random() < 0.08) {
        const rPool = nodeRefinedPoolRef.current ?? [];
        if (rPool.length > 0) {
          const rId   = rPool[Math.floor(Math.random() * rPool.length)];
          const rMeta = BASE_MATERIALS.find(m => m.id === rId);
          drops.push({ id: rId, isRefined: true, qty: 1, name: rMeta?.name ?? rId, color: rMeta?.color ?? "#ffcc44" });
        }
      }

      // ~3% chance of a discovered-recipe output drop (unlockMat recipes only)
      if (Math.random() < 0.03) {
        const disc = discoveredMaterialsRef.current;
        const pool = disc && disc.size > 0
          ? SMELT_RECIPES.filter(r => r.unlockMat && disc.has(r.unlockMat))
          : [];
        if (pool.length > 0) {
          const recipe = pool[Math.floor(Math.random() * pool.length)];
          const rMeta  = BASE_MATERIALS.find(m => m.id === recipe.output);
          if (rMeta) drops.push({ id: rMeta.id, isRefined: true, qty: 1, name: rMeta.name, color: rMeta.color });
        }
      }

      // Irregular polygon shape (random vertices)
      const verts = [];
      const nV = 5 + Math.floor(Math.random() * 4);
      for (let i = 0; i < nV; i++) {
        const a = (i / nV) * Math.PI * 2;
        const r = sz * (0.65 + Math.random() * 0.35);
        verts.push([Math.cos(a) * r, Math.sin(a) * r]);
      }

      st.debris.push({
        x: W / 2 + Math.cos(ang) * dist, y: H / 2 + Math.sin(ang) * dist,
        vx: Math.cos(oa) * sp, vy: Math.sin(oa) * sp,
        size: sz, hp, maxHp: hp,
        rot: Math.random() * Math.PI * 2,
        rotSpeed: (Math.random() - 0.5) * 0.008,
        drops, verts, hitFlash: 0,
        baseColor:   `hsl(${200 + Math.random() * 40},${10 + Math.random() * 15}%,${25 + Math.random() * 20}%)`,
        strokeColor: `hsl(${200 + Math.random() * 40},${15 + Math.random() * 20}%,${45 + Math.random() * 15}%)`,
      });
    }

    // ── Particle / float helpers (passed into module functions) ──────────────
    function addP(x, y, c, n = 4) {
      for (let i = 0; i < n; i++) {
        const a = Math.random() * Math.PI * 2, s = Math.random() * 1.8 + 0.3;
        st.particles.push({ x, y, vx: Math.cos(a) * s, vy: Math.sin(a) * s, life: 1, decay: 0.02 + Math.random() * 0.02, size: Math.random() * 2.5 + 0.8, color: c });
      }
    }
    function addF(x, y, t, c) {
      st.floats.push({ x, y, text: t, color: c, life: 1, vy: -0.8 });
    }

    // ── Pointer / touch input ─────────────────────────────────────────────────
    function getPos(e) {
      const r = canvas.getBoundingClientRect();
      if (e.touches        && e.touches.length > 0)        return { x: e.touches[0].clientX - r.left,        y: e.touches[0].clientY - r.top };
      if (e.changedTouches && e.changedTouches.length > 0) return { x: e.changedTouches[0].clientX - r.left, y: e.changedTouches[0].clientY - r.top };
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    }
    const oD = (e) => { e.preventDefault(); e.stopPropagation(); st.pointerDown = true;  const p = getPos(e); st.moveTargetX = p.x; st.moveTargetY = p.y; };
    const oM = (e) => { e.preventDefault(); e.stopPropagation(); if (st.pointerDown) { const p = getPos(e); st.moveTargetX = p.x; st.moveTargetY = p.y; } };
    const oU = (e) => { if (e.cancelable) e.preventDefault(); st.pointerDown = false; };
    canvas.addEventListener("touchstart",  oD, { passive: false });
    canvas.addEventListener("touchmove",   oM, { passive: false });
    canvas.addEventListener("touchend",    oU, { passive: false });
    canvas.addEventListener("touchcancel", oU, { passive: false });
    canvas.addEventListener("mousedown",   oD);
    canvas.addEventListener("mousemove",   oM);
    canvas.addEventListener("mouseup",     oU);
    canvas.addEventListener("mouseleave",  oU);

    // ── Main game loop ────────────────────────────────────────────────────────
    let combatInited = false;
    let lastNodeId   = null;

    function loop() {
      const s   = statsRef.current;
      const pks = perksRef.current;
      st.pulse += 0.04;
      st.spawnT++;
      // nano_repair: heal 0.05 HP per frame (~3 HP/s at 60fps)
      if (pks.some(pk => pk.id === "nano_repair") && playerHpRef.current > 0 && playerHpRef.current < playerMaxHpRef.current) {
        const prev = Math.floor(playerHpRef.current);
        playerHpRef.current = Math.min(playerMaxHpRef.current, playerHpRef.current + 0.05);
        if (Math.floor(playerHpRef.current) !== prev) setPlayerHp(Math.floor(playerHpRef.current));
      }

      // Center ship on first frame
      if (!shipInited) { st.shipX = cx; st.shipY = cy; shipInited = true; }

      // ── Node transition: reset combat state when the player moves ──────────
      const nodeId   = currentNodeRef.current;
      const inCombat = isEnemyNodeRef.current || nodeTypeRef.current === "gate_out";
      if (nodeId !== lastNodeId) {
        lastNodeId       = nodeId;
        combatInited     = false;
        st.enemies       = [];
        st.enemyBullets  = [];
        st.clearFired    = false;
        st.playerBullets = [];
        st.blasterTimer  = 0;
        st.telegraphs    = [];
        st.qtes          = [];
        st.coreHeat      = 0;
        st.coreHeatLocked = false;
        coreHeatRef.current = 0;
        st.hadGuardian   = false;
        st.guardianFired = false;
        // Restore shield to max when entering a new node
        st.shieldHp      = shieldMaxRef.current;
        st.shieldHitTimer = 0;
      }

      // Spawn enemy wave on first frame of a combat node
      if (inCombat && !combatInited) {
        const si = (sectorIdxRef && sectorIdxRef.current) ?? 0;
        initEnemies(st, si, W, H, nodeTypeRef.current === "gate_out");
        combatInited = true;
        st.combatMisses = 0;
      }

      // ── Tactical preset multipliers (combat.md §1) ──────────────────────
      const preset = TACTICAL_PRESETS[presetRef.current] ?? TACTICAL_PRESETS.balanced;
      const pDmg      = (preset.buffs.blasterDamage   ?? 1) * (preset.debuffs.blasterDamage   ?? 1);
      const pSpd      = (preset.buffs.shipSpeed       ?? 1) * (preset.debuffs.shipSpeed       ?? 1);
      const pShdMax   = (preset.buffs.shieldMax       ?? 1) * (preset.debuffs.shieldMax       ?? 1);
      const pShdRech  = (preset.buffs.shieldRecharge  ?? 1) * (preset.debuffs.shieldRecharge  ?? 1);

      // ── Effective stat values (base + perk + preset modifiers) ─────────────
      const eBlasterDmg      = (s.blasterDamage ?? 12) * pDmg;
      const eBlasterFireRate = Math.max(6,  s.blasterFireRate ?? 30);
      const eBlasterCount    = Math.max(1,  Math.round(s.blasterCount ?? 1));
      const eCurShieldMax    = (s.shieldMax ?? 0) * pShdMax;
      const eTractor         = TRACTOR_BASE + (s.attractRange ?? 0);
      const heatCfg = {
        max:      s.coreHeatMax      ?? 100,
        perShot:  s.coreHeatPerShot  ?? 1.5,
        coolRate: (s.coreHeatCoolRate ?? 0.5) * pShdRech, // defensive cools faster
      };
      const combatCfg = {
        qteSliderWidth:       s.qteSliderWidth       ?? 0.22,
        qteFrigateMitigation: s.qteFrigateMitigation ?? 0,
        qteBreachSlow:        s.qteBreachSlow        ?? 0,
      };
      let eSpawn = s.spawnRate, eDmg = s.laserDamage, eRange = s.laserRange, eRot = s.rotSpeed, eLoot = s.bonusLoot, eCrit = 0, eXpMult = 1, eShipSpeedMult = 1;
      for (const pk of pks) {
        if (pk.id === "dense_field")  eSpawn       *= 1.6;
        if (pk.id === "sparse")       eSpawn       *= 0.7;
        if (pk.id === "mine_fast")    eDmg         *= 1.3;
        if (pk.id === "slow_mine")    eDmg         *= 0.75;
        if (pk.id === "rich_salvage") eLoot        += 1;
        if (pk.id === "unstable")     eLoot        -= 1;
        if (pk.id === "corrosive")    eRange       *= 0.8;
        if (pk.id === "interference") eRot         *= 0.7;
        if (pk.id === "crit_strike")  eCrit         = 0.15;
        if (pk.id === "exp_gain")     eXpMult      *= 1.25;
        if (pk.id === "power_drain")  eShipSpeedMult *= 0.85;
      }
      const eShipSpeed = Math.max(0.5, (s.shipSpeed ?? 1.8) * pSpd * eShipSpeedMult);

      const tStep     = tutorialRef ? tutorialRef.current : 99;
      const spawnMult = devSpawnRateRef.current;
      const dead      = isDeadRef.current;

      // ── Debris spawning (suppressed during active combat) ──────────────────
      if (!dead) {
        if (tStep === 0) {
          // Tutorial step 0: waiting for the player to move — don't spawn yet
        } else if (!inCombat || st.enemies.length === 0) {
          const effectiveSpawn = tStep === 3 ? eSpawn * 0.5 : eSpawn * spawnMult;
          const spawnInterval  = Math.max(8, Math.floor(160 / effectiveSpawn));
          const debrisCap      = tStep === 3 ? 8 : (s.debrisCap ?? 8);
          if (st.spawnT >= spawnInterval && st.debris.length < debrisCap) {
            spawn(); st.spawnT = 0;
          }
        }
      }

      // ── Player movement (manual pointer-driven) ────────────────────────────
      // updatePlayerMovement returns { shipMoving, moveAngle } and also ages trail particles
      let { shipMoving, moveAngle } = updatePlayerMovement(
        st, eShipSpeed, W, H, onShipMoved, shipStartRef
      );
      // Initialise the start reference on the first frame so distance tracking works
      if (shipStartRef.current === null && shipInited) {
        shipStartRef.current = { x: st.shipX, y: st.shipY };
      }

      // ── Mining laser target (debris, only when no enemies are alive) ────────
      let laserTarget = null, laserTargetDist = Infinity;
      if (!dead && (!inCombat || st.enemies.length === 0)) {
        for (const d of st.debris) {
          const dx = d.x - st.shipX, dy = d.y - st.shipY;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < eRange + d.size && dist < laserTargetDist) {
            laserTarget = d; laserTargetDist = dist;
          }
        }
        if (laserTarget) st.targetAngle = Math.atan2(laserTarget.y - st.shipY, laserTarget.x - st.shipX);
      }

      // ── Blaster target + laser angle lerp (enemies only) ───────────────────
      // updateBlasterTarget also calls aLerp to rotate st.laserAngle toward the target
      let blasterTarget = null;
      if (!dead) {
        blasterTarget = updateBlasterTarget(st, inCombat, eRot);
      } else {
        // Still lerp angle while dead so it doesn't snap on respawn
        st.laserAngle = aLerp(st.laserAngle, st.targetAngle, eRot);
      }

      // ── Player blaster firing ──────────────────────────────────────────────
      if (!dead) updateBlasterFire(st, blasterTarget, eBlasterDmg, eBlasterFireRate, eBlasterCount, addP, heatCfg);
      coreHeatRef.current = st.coreHeat ?? 0;

      // ── Player bullet movement + enemy hit detection ───────────────────────
      updatePlayerBullets(st, W, H, addP);

      // ── Shield recharge ────────────────────────────────────────────────────
      updateShieldRecharge(st, eCurShieldMax, shieldMaxRef, setPlayerShield);

      // ── Auto-nav (steers toward nearest debris when toggled) ───────────────
      if (!dead) {
        ({ shipMoving, moveAngle } = updateAutoNav(
          st, autoNavRef.current, inCombat, eShipSpeed, eRange, W, H, shipMoving, moveAngle
        ));
      }

      // ── Drones ─────────────────────────────────────────────────────────────
      const eDroneSpeed = eShipSpeed * 0.85;
      // Add/remove drones to match the current count setting
      while (st.drones.length < droneCountRef.current) {
        st.drones.push({ x: st.shipX, y: st.shipY, angle: Math.random() * Math.PI * 2, targetAngle: 0, trail: [], target: null });
      }
      while (st.drones.length > droneCountRef.current) st.drones.pop();

      const assignedDebris = new Set(); // prevent two drones targeting the same piece
      for (const drone of st.drones) {
        // Drones target enemies during combat, debris outside combat
        const targetPool = (inCombat && st.enemies.length > 0) ? st.enemies : st.debris;
        let tgt = null, tgtDist = Infinity;
        for (const d of targetPool) {
          if (assignedDebris.has(d)) continue;
          const dx = d.x - drone.x, dy = d.y - drone.y;
          const dd = Math.sqrt(dx * dx + dy * dy);
          if (dd < tgtDist) { tgt = d; tgtDist = dd; }
        }
        if (!tgt && targetPool.length > 0) {
          tgt = targetPool[0];
          tgtDist = Math.sqrt((tgt.x - drone.x) ** 2 + (tgt.y - drone.y) ** 2);
        }
        if (tgt) assignedDebris.add(tgt);
        drone.target = tgt;

        if (tgt) {
          // Approach if outside attack range
          if (tgtDist > DRONE_RANGE * 0.6) {
            const dx = tgt.x - drone.x, dy = tgt.y - drone.y;
            drone.x += (dx / tgtDist) * eDroneSpeed;
            drone.y += (dy / tgtDist) * eDroneSpeed;
            drone.x = Math.max(12, Math.min(W - 12, drone.x));
            drone.y = Math.max(12, Math.min(H - 12, drone.y));
            if (Math.random() < 0.3) {
              const ta = Math.atan2(dy, dx) + Math.PI + (Math.random() - 0.5) * 0.5;
              drone.trail.push({ x: drone.x + Math.cos(ta) * 6, y: drone.y + Math.sin(ta) * 6, life: 1 });
            }
          }
          drone.targetAngle = Math.atan2(tgt.y - drone.y, tgt.x - drone.x);
          drone.angle = aLerp(drone.angle, drone.targetAngle, 0.12);

          // Deal damage when beam is on-target
          const lx = tgt.x - drone.x, ly = tgt.y - drone.y, ld = Math.sqrt(lx * lx + ly * ly);
          if (ld < DRONE_RANGE + (tgt.size ?? 10)) {
            let ad = Math.atan2(ly, lx) - drone.angle;
            while (ad >  Math.PI) ad -= Math.PI * 2;
            while (ad < -Math.PI) ad += Math.PI * 2;
            if (Math.abs(Math.sin(ad) * ld) < (tgt.size ?? 10) + 2 && Math.cos(ad) > 0) {
              tgt.hp -= eDmg * 0.09; tgt.hitFlash = 0.3;
              if (Math.random() < 0.1) addP(tgt.x + (Math.random() - 0.5) * (tgt.size ?? 10), tgt.y + (Math.random() - 0.5) * (tgt.size ?? 10), "#ffaa33", 1);
            }
          }
        }
        // Age drone trail particles
        for (let i = drone.trail.length - 1; i >= 0; i--) {
          drone.trail[i].life -= 0.045;
          if (drone.trail[i].life <= 0) drone.trail.splice(i, 1);
        }
      }

      const shipHeading = shipMoving ? moveAngle : st.laserAngle;

      // ── Enemy AI update (movement, orbit, firing, death) ──────────────────
      if (!dead) {
        const si = (sectorIdxRef && sectorIdxRef.current) ?? 0;
        updateEnemies(
          st, W, H, si,
          addP, addF,
          setMaterials, onItemDropRef.current,
          () => { if (onDebrisDestroyed) onDebrisDestroyed(); if (onEnemyKillRef.current) onEnemyKillRef.current(); },
          addXpRef.current, statsRef.current.xpPerKill ?? 0,
          nodeEchoMultRef?.current ?? 1,
          combatCfg
        );

        // ── Telegraphs (Lane / Pulse / Sweep / Safe Pocket) ──────────────────
        const tDmg = updateTelegraphs(st, W, H);
        if (tDmg > 0 && st.iFrames === 0) {
          // Apply to shield first, then hull. Mirrors updateEnemyBullets.
          let dmgLeft = tDmg;
          if (st.shieldHp > 0) {
            const absorbed = Math.min(st.shieldHp, dmgLeft);
            st.shieldHp       = Math.max(0, st.shieldHp - absorbed);
            dmgLeft          -= absorbed;
            st.shieldHitTimer = SHIELD_RECHARGE_DELAY;
            addF(st.shipX, st.shipY - 20, `-${Math.round(absorbed)} SHD`, "#66aaff");
          }
          if (dmgLeft > 0) {
            const newHp = Math.max(0, playerHpRef.current - dmgLeft);
            addF(st.shipX, st.shipY - 34, `-${Math.round(dmgLeft)} HP`, "#ff4444");
            setHp(newHp, st.shieldHp);
            if (newHp <= 0 && !isDeadRef.current) {
              isDeadRef.current = true; setIsDead(true);
              addP(st.shipX, st.shipY, "#ff2222", 30);
              if (onPlayerDeathRef.current) onPlayerDeathRef.current();
            }
          } else {
            setHp(playerHpRef.current, st.shieldHp);
          }
          st.iFrames = 20;
          st.combatMisses = (st.combatMisses ?? 0) + 1;
        }

        // Advance QTE timers (ms-based; assume 60fps → ~16.6ms/frame)
        updateQtes(st, 16.6);

        // Update HUD enemy count
        setEnemiesLeft(st.enemies.length);

        // Guardian kill detection — if the guardian was present last frame but
        // gone now, the player has cleared the boss. Fires the parent callback
        // so the starmap allows the sector advance.
        const guardianAlive = st.enemies.some(e => e.archetype === "guardian");
        if (st.hadGuardian && !guardianAlive && !st.guardianFired) {
          st.guardianFired = true;
          if (onGuardianDownRef.current) onGuardianDownRef.current();
        }
        if (guardianAlive) st.hadGuardian = true;

        // Show "SECTOR CLEAR" banner when last enemy dies
        if (inCombat && combatInited && st.enemies.length === 0 && !st.clearFired) {
          st.clearFired = true;
          setShowClearBanner(true);
          if (clearBannerTimerRef.current) clearTimeout(clearBannerTimerRef.current);
          clearBannerTimerRef.current = setTimeout(() => setShowClearBanner(false), 4000);

          // Perfect Streak (combat.md §4): no misses this fight → 1.5x reward
          const misses = st.combatMisses ?? 0;
          if (misses === 0) {
            streakRef.current = (streakRef.current ?? 0) + 1;
            const bonus = Math.round(10 * streakRef.current * 1.5);
            if (setVoidCreds) setVoidCreds(v => v + bonus);
            addF(st.shipX, st.shipY - 50, `PERFECT +${bonus} VC`, "#bb99ff");
          } else {
            streakRef.current = 0;
            if (setVoidCreds) setVoidCreds(v => v + 10);
          }
        }
      }

      // ── Enemy bullet movement + player hit detection ───────────────────────
      updateEnemyBullets(
        st, W, H, dead,
        playerHpRef, isDeadRef,
        SHIELD_RECHARGE_DELAY,
        setHp, setIsDead, onPlayerDeathRef.current,
        addP, addF
      );

      // ── Debris physics + laser damage + destruction ────────────────────────
      if (!inCombat || st.enemies.length === 0) {
        for (let i = st.debris.length - 1; i >= 0; i--) {
          const d = st.debris[i];
          // Drift toward center of arena with gentle gravity
          const dx = W / 2 - d.x, dy = H / 2 - d.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          d.x += d.vx; d.y += d.vy; d.rot += d.rotSpeed;
          if (d.hitFlash > 0) d.hitFlash -= 0.08;
          if (dist > Math.min(W, H) * 0.4) { d.vx += dx / dist * 0.005; d.vy += dy / dist * 0.005; }
          // Soft repulsion when ship is very close (prevents clipping)
          const sdx = st.shipX - d.x, sdy = st.shipY - d.y;
          const sdist = Math.sqrt(sdx * sdx + sdy * sdy);
          if (sdist < 40 + d.size) { d.vx -= sdx / sdist * 0.008; d.vy -= sdy / sdist * 0.008; }
          // Passive auto-break stat chips away HP each frame
          if (s.autoBreak > 0) d.hp -= s.autoBreak * 0.3;
          d.vx *= 0.999; d.vy *= 0.999;
          // Bounce off canvas edges
          const margin = d.size;
          if (d.x < margin)     { d.x = margin;     d.vx =  Math.abs(d.vx); }
          if (d.x > W - margin) { d.x = W - margin; d.vx = -Math.abs(d.vx); }
          if (d.y < margin)     { d.y = margin;      d.vy =  Math.abs(d.vy); }
          if (d.y > H - margin) { d.y = H - margin;  d.vy = -Math.abs(d.vy); }
          // Despawn if too far from center (shouldn't normally happen)
          if (dist > Math.min(W, H) * 0.7) { st.debris.splice(i, 1); continue; }

          // Mining laser hit — narrow cone check against the laser angle
          if (!dead && laserTarget && laserTarget === d) {
            const lx = d.x - st.shipX, ly = d.y - st.shipY, ld = Math.sqrt(lx * lx + ly * ly);
            if (ld < eRange + d.size) {
              let ad = Math.atan2(ly, lx) - st.laserAngle;
              while (ad >  Math.PI) ad -= Math.PI * 2;
              while (ad < -Math.PI) ad += Math.PI * 2;
              if (Math.abs(Math.sin(ad) * ld) < d.size * s.beamWidth + 3 && Math.cos(ad) > 0) {
                const critHit = eCrit > 0 && Math.random() < eCrit;
                d.hp -= critHit ? d.hp : eDmg * 0.12; d.hitFlash = 0.4;
                if (Math.random() < 0.15) addP(d.x + (Math.random() - 0.5) * d.size, d.y + (Math.random() - 0.5) * d.size, d.strokeColor, 1);
              }
            }
          }

          // Debris destroyed — spawn a wreck for tractor collection
          if (d.hp <= 0) {
            if (onDebrisDestroyed) onDebrisDestroyed();
            if (addXpRef.current) addXpRef.current(Math.round((8 + (statsRef.current.xpPerMine ?? 0)) * eXpMult));
            // Echo rolls fire immediately on destruction — no tractor required
            if (onItemDropRef.current) {
              for (const id of d.drops.map(dr => dr.id)) onItemDropRef.current(id);
            }
            addP(d.x, d.y, d.strokeColor, 12); addP(d.x, d.y, "#fff", 4);
            const driftAng = Math.random() * Math.PI * 2;
            st.wrecks.push({
              x: d.x, y: d.y,
              vx: Math.cos(driftAng) * WRECK_DRIFT * (0.5 + Math.random()),
              vy: Math.sin(driftAng) * WRECK_DRIFT * (0.5 + Math.random()),
              size: d.size * 0.55,
              rot: d.rot, rotSpeed: d.rotSpeed * 0.5,
              drops: d.drops, eLoot,
              life: WRECK_LIFE,
              pulseOff: Math.random() * Math.PI * 2,
              color: d.strokeColor,
              dropsForFragment: [], // echoes already rolled above
            });
            st.debris.splice(i, 1);
          }
        }
      }

      // ── Wreck tractor-beam collection ─────────────────────────────────────
      for (let i = st.wrecks.length - 1; i >= 0; i--) {
        const w = st.wrecks[i];
        w.life--;
        w.rot += w.rotSpeed;

        const dx   = st.shipX - w.x, dy = st.shipY - w.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        // Pull toward ship when inside tractor range
        if (dist < eTractor) {
          const pull = TRACTOR_PULL * (1 - dist / eTractor) + 0.15;
          w.vx += (dx / dist) * pull;
          w.vy += (dy / dist) * pull;
        } else {
          w.vx *= 0.995; w.vy *= 0.995; // slow drift outside range
        }
        w.x += w.vx; w.y += w.vy;

        // Collect wreck when close enough
        if (dist < 18) {
          const rawDrops      = w.drops.filter(dr => !dr.isRefined);
          const refinedDrops  = w.drops.filter(dr =>  dr.isRefined);
          let cc = 0;
          if (rawDrops.length > 0) {
            setInventory(prev => {
              const updated = { ...prev };
              for (const dr of rawDrops) {
                const q = Math.round(Math.max(0, dr.qty + (w.eLoot ?? 0)));
                if (q > 0) { updated[dr.id] = (updated[dr.id] || 0) + q; }
              }
              return updated;
            });
          }
          if (refinedDrops.length > 0) {
            setMaterials(prev => {
              const updated = { ...prev };
              for (const dr of refinedDrops) {
                if (dr.qty > 0) { updated[dr.id] = (updated[dr.id] || 0) + dr.qty; }
              }
              return updated;
            });
          }
          // Float text for all drops
          for (const dr of w.drops) {
            const q = dr.isRefined ? dr.qty : Math.round(Math.max(0, dr.qty + (w.eLoot ?? 0)));
            if (q > 0) {
              const label = dr.name ?? dr.id;
              const col   = dr.isRefined ? "#ffcc44" : (dr.color ?? "#aaa");
              addF(w.x, w.y - (w.size ?? 10) - cc * 16, `+${fmt(q)} ${label}`, col);
              cc++;
            }
          }
          addP(st.shipX, st.shipY, "#00ccff", 5);
          st.wrecks.splice(i, 1);
          continue;
        }

        // Wreck expires if not collected in time
        if (w.life <= 0) { addP(w.x, w.y, "#ff4444", 3); st.wrecks.splice(i, 1); continue; }

        // Bounce wrecks off canvas edges
        const ws = w.size ?? 10;
        if (w.x < ws)     { w.x = ws;     w.vx =  Math.abs(w.vx); }
        if (w.x > W - ws) { w.x = W - ws; w.vx = -Math.abs(w.vx); }
        if (w.y < ws)     { w.y = ws;      w.vy =  Math.abs(w.vy); }
        if (w.y > H - ws) { w.y = H - ws;  w.vy = -Math.abs(w.vy); }
      }

      // Age particles and floats
      for (let i = st.particles.length - 1; i >= 0; i--) { const p = st.particles[i]; p.x += p.vx; p.y += p.vy; p.life -= p.decay; if (p.life <= 0) st.particles.splice(i, 1); }
      for (let i = st.floats.length   - 1; i >= 0; i--) { const f = st.floats[i]; f.y += f.vy; f.life -= 0.012; if (f.life <= 0) st.floats.splice(i, 1); }

      // ── DRAW ──────────────────────────────────────────────────────────────
      ctx.clearRect(0, 0, W, H);

      // Background gradient — node-type themed, reddish tint when enemies are alive
      const theme = (inCombat && st.enemies.length > 0)
        ? NODE_THEMES.combat_zone
        : (NODE_THEMES[nodeTypeRef.current] ?? NODE_THEMES.default);
      const [c0, c1, c2] = theme.bg;
      const bg = ctx.createRadialGradient(cx, cy, 30, cx, cy, Math.max(W, H) * 0.7);
      bg.addColorStop(0, c0); bg.addColorStop(0.6, c1); bg.addColorStop(1, c2);
      ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);

      // Animated nebulae
      for (const nb of theme.nebulae) {
        const nx = (nb.ox + Math.sin(st.pulse * 0.08 + nb.ox * 5) * 0.04) * W;
        const ny = (nb.oy + Math.cos(st.pulse * 0.06 + nb.oy * 5) * 0.04) * H;
        const nr = Math.max(W, H) * nb.r;
        const gd = ctx.createRadialGradient(nx, ny, 0, nx, ny, nr);
        gd.addColorStop(0, `rgba(${nb.rgb},${nb.a})`);
        gd.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = gd; ctx.fillRect(0, 0, W, H);
      }

      // Stars
      st.stars.forEach(star => {
        ctx.fillStyle = `rgba(${theme.star},${0.25 + Math.sin(st.pulse * 0.4 + star.b * 12) * 0.18})`;
        ctx.beginPath(); ctx.arc(star.x * W, star.y * H, star.s, 0, Math.PI * 2); ctx.fill();
      });

      // Move target indicator
      if (st.moveTargetX !== null && st.moveTargetY !== null && shipMoving) {
        ctx.strokeStyle = "rgba(0,180,255,0.15)"; ctx.lineWidth = 1; ctx.setLineDash([3, 6]);
        ctx.beginPath(); ctx.arc(st.moveTargetX, st.moveTargetY, 8 + Math.sin(st.pulse * 3) * 2, 0, Math.PI * 2); ctx.stroke(); ctx.setLineDash([]);
        ctx.strokeStyle = "rgba(0,180,255,0.06)"; ctx.lineWidth = 0.5;
        ctx.beginPath(); ctx.moveTo(st.shipX, st.shipY); ctx.lineTo(st.moveTargetX, st.moveTargetY); ctx.stroke();
      }

      // Laser range ring (faint, only when targeting)
      if (!dead && laserTarget) {
        ctx.strokeStyle = inCombat ? "rgba(255,50,50,0.06)" : "rgba(255,80,50,0.04)";
        ctx.lineWidth = 1; ctx.setLineDash([4, 8]);
        ctx.beginPath(); ctx.arc(st.shipX, st.shipY, eRange, 0, Math.PI * 2); ctx.stroke(); ctx.setLineDash([]);
      }

      // Tractor range ring — brightens when wrecks are within range
      const wrecksInRange = st.wrecks.some(w => Math.sqrt((w.x - st.shipX) ** 2 + (w.y - st.shipY) ** 2) < eTractor);
      ctx.strokeStyle = wrecksInRange
        ? `rgba(0,200,255,${0.18 + Math.sin(st.pulse * 3) * 0.08})`
        : "rgba(0,160,220,0.06)";
      ctx.lineWidth = wrecksInRange ? 1.2 : 0.7;
      ctx.setLineDash([5, 8]);
      ctx.beginPath(); ctx.arc(st.shipX, st.shipY, eTractor, 0, Math.PI * 2); ctx.stroke();
      ctx.setLineDash([]);

      // Tractor beam lines toward each wreck in range
      if (wrecksInRange) {
        for (const w of st.wrecks) {
          const wd = Math.sqrt((w.x - st.shipX) ** 2 + (w.y - st.shipY) ** 2);
          if (wd < eTractor) {
            const alpha = (1 - wd / eTractor) * 0.45;
            ctx.save();
            ctx.strokeStyle = `rgba(0,200,255,${alpha})`; ctx.lineWidth = 1; ctx.setLineDash([3, 5]);
            ctx.beginPath(); ctx.moveTo(st.shipX, st.shipY); ctx.lineTo(w.x, w.y); ctx.stroke();
            ctx.setLineDash([]);
            ctx.restore();
          }
        }
      }

      // ── Active debris ──
      if (!inCombat || st.enemies.length === 0) {
        st.debris.forEach(d => {
          ctx.save(); ctx.translate(d.x, d.y); ctx.rotate(d.rot);
          ctx.fillStyle   = d.hitFlash > 0 ? `rgba(255,255,255,${d.hitFlash})` : d.baseColor;
          ctx.strokeStyle = d.strokeColor; ctx.lineWidth = 1;
          ctx.beginPath(); ctx.moveTo(d.verts[0][0], d.verts[0][1]);
          for (let v = 1; v < d.verts.length; v++) ctx.lineTo(d.verts[v][0], d.verts[v][1]);
          ctx.closePath(); ctx.fill(); ctx.stroke();
          // Drop indicators (coloured dots)
          d.drops.forEach((dr, di) => {
            ctx.fillStyle = dr.color; ctx.shadowColor = dr.color; ctx.shadowBlur = 4;
            ctx.beginPath(); ctx.arc((di - (d.drops.length - 1) / 2) * 6, 0, 2, 0, Math.PI * 2); ctx.fill(); ctx.shadowBlur = 0;
          });
          // HP bar (only visible when damaged)
          const pct = d.hp / d.maxHp;
          if (pct < 1) {
            const bw = d.size * 1.2;
            ctx.fillStyle = "rgba(0,0,0,0.5)"; ctx.fillRect(-bw / 2, -d.size - 7, bw, 3);
            ctx.fillStyle = pct > 0.5 ? "#4fc3f7" : pct > 0.25 ? "#ffa726" : "#ff5555";
            ctx.fillRect(-bw / 2, -d.size - 7, bw * pct, 3);
          }
          ctx.restore();
        });
      }

      // ── Wrecks (destroyed debris / enemy corpses) ──
      for (const w of st.wrecks) {
        const lifeFrac = w.life / WRECK_LIFE;
        const wd       = Math.sqrt((w.x - st.shipX) ** 2 + (w.y - st.shipY) ** 2);
        const inRange  = wd < eTractor;
        const pulse    = 0.4 + Math.sin(st.pulse * 2.5 + w.pulseOff) * 0.15;
        const wSize    = w.size ?? 10;
        ctx.save();
        ctx.translate(w.x, w.y); ctx.rotate(w.rot);
        ctx.globalAlpha = Math.min(1, lifeFrac * 3) * (inRange ? 1 : 0.65);
        const wColor = w.isEnemyWreck ? "255,80,50" : "0,180,255";
        ctx.fillStyle   = inRange ? `rgba(${wColor},0.08)` : "rgba(60,80,100,0.25)";
        ctx.strokeStyle = inRange ? `rgba(${wColor},${pulse})` : `rgba(100,80,70,${lifeFrac * 0.5})`;
        ctx.lineWidth   = inRange ? 1.5 : 0.8;
        ctx.beginPath();
        const sides = w.isEnemyWreck ? 4 : 6;
        for (let k = 0; k < sides; k++) {
          const a = (k / sides) * Math.PI * 2;
          const r = wSize * (0.6 + ((k * 17 + 3) % 7) / 7 * 0.4);
          k === 0 ? ctx.moveTo(Math.cos(a) * r, Math.sin(a) * r) : ctx.lineTo(Math.cos(a) * r, Math.sin(a) * r);
        }
        ctx.closePath(); ctx.fill(); ctx.stroke();
        w.drops.forEach((dr, di) => {
          ctx.fillStyle = dr.color; ctx.shadowColor = dr.color; ctx.shadowBlur = inRange ? 5 : 2;
          ctx.globalAlpha = inRange ? 0.9 : 0.4;
          ctx.beginPath(); ctx.arc((di - (w.drops.length - 1) / 2) * 5, 0, 2, 0, Math.PI * 2); ctx.fill();
          ctx.shadowBlur = 0;
        });
        ctx.globalAlpha = 1;
        // Life-remaining bar (only when out of tractor range)
        if (!inRange) {
          const bw = wSize * 1.2;
          ctx.globalAlpha = 0.5;
          ctx.fillStyle = "rgba(0,0,0,0.4)"; ctx.fillRect(-bw / 2, -wSize - 6, bw, 2);
          ctx.fillStyle = lifeFrac > 0.5 ? "#ffaa22" : "#ff4444"; ctx.fillRect(-bw / 2, -wSize - 6, bw * lifeFrac, 2);
          ctx.globalAlpha = 1;
        }
        ctx.restore();
      }

      // ── Telegraphs (behind ships so ships render on top) ──
      drawTelegraphs(ctx, st.telegraphs, W, H);

      // ── Enemy ships and bullets (from enemyLogic.js) ──
      drawEnemies(ctx, st.enemies, st.pulse);
      drawEnemyBullets(ctx, st.enemyBullets);

      // ── Engine trail + player ship + blaster bolts (from playerShip.js) ──
      drawEngineTrail(ctx, st.engineTrail);

      // Mining laser beam (active only when not in combat and targeting debris)
      if (!dead && laserTarget && (!inCombat || st.enemies.length === 0)) {
        const ex = st.shipX + Math.cos(st.laserAngle) * eRange;
        const ey = st.shipY + Math.sin(st.laserAngle) * eRange;
        const isP = s.plasmaVisual;
        ctx.save(); ctx.lineCap = "round";
        ctx.strokeStyle = isP ? "rgba(50,120,255,0.12)"  : "rgba(255,50,50,0.12)";  ctx.lineWidth = 10 * s.beamWidth; ctx.beginPath(); ctx.moveTo(st.shipX, st.shipY); ctx.lineTo(ex, ey); ctx.stroke();
        ctx.strokeStyle = isP ? "rgba(80,160,255,0.4)"   : "rgba(255,80,60,0.35)";  ctx.lineWidth = 3.5 * s.beamWidth; ctx.beginPath(); ctx.moveTo(st.shipX, st.shipY); ctx.lineTo(ex, ey); ctx.stroke();
        ctx.strokeStyle = isP ? "#66bbff" : "#ff8866"; ctx.lineWidth = 1.2 * s.beamWidth; ctx.shadowColor = isP ? "#4499ff" : "#ff4422"; ctx.shadowBlur = 8;
        ctx.beginPath(); ctx.moveTo(st.shipX, st.shipY); ctx.lineTo(ex, ey); ctx.stroke(); ctx.shadowBlur = 0;
        const tg = ctx.createRadialGradient(ex, ey, 0, ex, ey, 6);
        tg.addColorStop(0, isP ? "rgba(100,180,255,0.5)" : "rgba(255,100,60,0.5)"); tg.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = tg; ctx.beginPath(); ctx.arc(ex, ey, 6, 0, Math.PI * 2); ctx.fill();
        if (Math.random() < 0.25) addP(ex, ey, isP ? "#88bbff" : "#ff6644", 1);
        ctx.restore();
      }

      drawPlayerShip(ctx, st, dead, shipHeading, shipMoving);
      drawPlayerBullets(ctx, st.playerBullets);

      // ── Drones ──
      for (const drone of st.drones) {
        drone.trail.forEach(t => { ctx.globalAlpha = t.life * 0.45; ctx.fillStyle = `rgba(255,160,0,${t.life})`; ctx.beginPath(); ctx.arc(t.x, t.y, 1.2, 0, Math.PI * 2); ctx.fill(); });
        ctx.globalAlpha = 1;
        const dt = drone.target && ([...st.debris, ...st.enemies].includes(drone.target)) ? drone.target : null;
        if (dt) {
          const ex = drone.x + Math.cos(drone.angle) * DRONE_RANGE, ey = drone.y + Math.sin(drone.angle) * DRONE_RANGE;
          ctx.save(); ctx.lineCap = "round";
          ctx.strokeStyle = "rgba(255,180,0,0.1)";  ctx.lineWidth = 7;   ctx.beginPath(); ctx.moveTo(drone.x, drone.y); ctx.lineTo(ex, ey); ctx.stroke();
          ctx.strokeStyle = "rgba(255,160,0,0.45)"; ctx.lineWidth = 2;   ctx.beginPath(); ctx.moveTo(drone.x, drone.y); ctx.lineTo(ex, ey); ctx.stroke();
          ctx.strokeStyle = "#ffcc44"; ctx.lineWidth = 0.8; ctx.shadowColor = "#ffaa00"; ctx.shadowBlur = 6;
          ctx.beginPath(); ctx.moveTo(drone.x, drone.y); ctx.lineTo(ex, ey); ctx.stroke(); ctx.shadowBlur = 0;
          ctx.restore();
        }
        ctx.save(); ctx.translate(drone.x, drone.y);
        const dg = ctx.createRadialGradient(0, 0, 2, 0, 0, 13);
        dg.addColorStop(0, "rgba(255,170,0,0.18)"); dg.addColorStop(1, "rgba(255,80,0,0)");
        ctx.fillStyle = dg; ctx.beginPath(); ctx.arc(0, 0, 13, 0, Math.PI * 2); ctx.fill();
        ctx.rotate(drone.angle + Math.PI / 2);
        ctx.fillStyle = "#1a1208"; ctx.strokeStyle = "#ffaa33"; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(0, -9); ctx.lineTo(-5, 4); ctx.lineTo(-3, 8); ctx.lineTo(3, 8); ctx.lineTo(5, 4); ctx.closePath(); ctx.fill(); ctx.stroke();
        ctx.fillStyle = "#ffcc44"; ctx.shadowColor = "#ffaa00"; ctx.shadowBlur = 5;
        ctx.beginPath(); ctx.ellipse(0, -3, 1.5, 2.5, 0, 0, Math.PI * 2); ctx.fill(); ctx.shadowBlur = 0;
        ctx.restore();
      }

      // Particles and floating text
      st.particles.forEach(p => { ctx.globalAlpha = p.life; ctx.fillStyle = p.color; ctx.beginPath(); ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2); ctx.fill(); });
      ctx.globalAlpha = 1;
      ctx.font = '13px system-ui, -apple-system, sans-serif'; ctx.textAlign = "center";
      st.floats.forEach(f => { ctx.globalAlpha = f.life; ctx.fillStyle = f.color; ctx.shadowColor = f.color; ctx.shadowBlur = 4; ctx.fillText(f.text, f.x, f.y); ctx.shadowBlur = 0; });
      ctx.globalAlpha = 1;

      // Context hint text at the bottom
      if (!dead && (!inCombat || st.enemies.length === 0) && st.debris.length > 0 && !st.pointerDown && st.moveTargetX === null) {
        const hasWrecks = st.wrecks.length > 0;
        ctx.globalAlpha = 0.5 + Math.sin(st.pulse * 2) * 0.1;
        ctx.fillStyle = "#99bbcc";
        ctx.fillText(hasWrecks ? "Move into wrecks — tractor beam collects them" : "Touch to move · Laser auto-fires", cx, H - 16);
        ctx.globalAlpha = 1;
      }
      if (!dead && inCombat && st.enemies.length > 0) {
        ctx.globalAlpha = 0.6 + Math.sin(st.pulse * 3) * 0.15;
        ctx.fillStyle = "#ff6644";
        ctx.fillText("⚠ HOSTILES DETECTED — DESTROY ALL ENEMIES", cx, H - 16);
        ctx.globalAlpha = 1;
      }

      animId = requestAnimationFrame(loop);
    }

    animId = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── HUD (React render) ───────────────────────────────────────────────────────
  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", position: "relative", overflow: "hidden" }}>
      <canvas ref={canvasRef} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", display: "block", touchAction: "none", zIndex: 1 }} />

      {/* Player HP + Shield HUD */}
      {(isEnemyNode || playerHp < playerMaxHp) && (
        <div style={{ position: "absolute", top: 6, left: "50%", transform: "translateX(-50%)", zIndex: 3, pointerEvents: "none", width: 180, background: "rgba(0,0,0,0.7)", padding: "4px", borderRadius: "6px", border: "1px solid rgba(255,255,255,0.1)", boxShadow: "0 4px 10px rgba(0,0,0,0.5)" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr", alignItems: "center" }}>
            {/* Hull + shield bars stacked (shield overlays hull) */}
            <div style={{ gridArea: "1/1", height: 12, background: "rgba(255,255,255,0.05)", borderRadius: 3, position: "relative", overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${(playerHp / playerMaxHp) * 100}%`, background: playerHp / playerMaxHp > 0.5 ? "#11aa66" : playerHp / playerMaxHp > 0.25 ? "#aa6611" : "#aa2222", transition: "width 0.3s ease-out, background 0.3s" }} />
              {shieldMax > 0 && (
                <div style={{ position: "absolute", top: 0, left: 0, height: "100%", width: `${(playerShield / shieldMax) * 100}%`, background: "linear-gradient(to bottom, rgba(68,136,255,0.7), rgba(68,136,255,0.4))", borderRight: playerShield > 0 ? "2px solid #66aaff" : "none", transition: "width 0.2s ease-out, opacity 0.3s", opacity: playerShield > 0 ? 1 : 0, boxShadow: "inset 0 0 10px rgba(100,200,255,0.5)", zIndex: 2 }} />
              )}
            </div>
            {/* Label overlay */}
            <div style={{ gridArea: "1/1", zIndex: 3, display: "flex", justifyContent: "space-between", padding: "0 6px", fontSize: "9px", fontWeight: "bold", color: "white", textShadow: "1px 1px 2px black", pointerEvents: "none" }}>
              <span>HULL: {Math.ceil(playerHp)}</span>
              {shieldMax > 0 && playerShield > 0 && <span style={{ color: "#66ccff" }}>SHIELD: {Math.ceil(playerShield)}</span>}
            </div>
          </div>
        </div>
      )}

      {/* Enemy count badge */}
      {isEnemyNode && !isDead && enemiesLeft > 0 && (
        <div style={{ position: "absolute", top: 60, left: "50%", transform: "translateX(-50%)", zIndex: 3, pointerEvents: "none", background: "rgba(180,20,20,0.8)", border: "1px solid rgba(255,80,50,0.5)", borderRadius: 6, padding: "3px 10px", fontSize: 11, fontWeight: 700, color: "#ffccbb", letterSpacing: 0.5 }}>
          ⚔ {enemiesLeft} HOSTILE{enemiesLeft !== 1 ? "S" : ""}
        </div>
      )}

      {/* Sector clear banner */}
      {isEnemyNode && showClearBanner && !isDead && (
        <div style={{ position: "absolute", top: "35%", left: "50%", transform: "translateX(-50%)", zIndex: 5, textAlign: "center" }}>
          <div style={{ position: "relative", background: "rgba(0,40,20,0.92)", border: "1px solid rgba(50,255,120,0.5)", borderRadius: 10, padding: "14px 36px 14px 28px", boxShadow: "0 0 30px rgba(50,255,120,0.25)" }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: "#44ff99", letterSpacing: 1, marginBottom: 4 }}>SECTOR CLEAR</div>
            <div style={{ fontSize: 12, color: "rgba(150,255,180,0.8)" }}>All hostiles destroyed — collect wrecks</div>
            <button onClick={() => { setShowClearBanner(false); if (clearBannerTimerRef.current) clearTimeout(clearBannerTimerRef.current); }}
              style={{ position: "absolute", top: 6, right: 8, background: "none", border: "none", color: "rgba(100,220,140,0.6)", fontSize: 16, cursor: "pointer", lineHeight: 1, padding: 2 }}>✕</button>
          </div>
        </div>
      )}

      {/* Death screen */}
      {isDead && (
        <div style={{ position: "absolute", inset: 0, zIndex: 10, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)" }}>
          <div style={{ textAlign: "center", background: "rgba(60,0,0,0.9)", border: "1px solid rgba(255,50,50,0.4)", borderRadius: 14, padding: "28px 36px", boxShadow: "0 0 40px rgba(255,0,0,0.2)" }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>💀</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#ff4444", letterSpacing: 1, marginBottom: 6 }}>SHIP DESTROYED</div>
            <div style={{ fontSize: 13, color: "rgba(255,180,160,0.8)", marginBottom: 20 }}>Returning to sector gate…</div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)" }}>Open the map to navigate</div>
          </div>
        </div>
      )}

      {/* Tactical preset selector, Core Heat, streak, and active QTEs */}
      <CombatOverlay
        stRef={stRefExt}
        activePreset={tacticalPreset}
        onPresetChange={setTacticalPreset}
        onResolveQte={handleResolveQte}
        coreHeatRef={coreHeatRef}
        coreHeatMax={statsRef.current?.coreHeatMax ?? 100}
        isEnemyNode={isEnemyNode || isGuardianNode}
        streakRef={streakRef}
      />

      {/* Autopilot / Drone controls — shown only when researched */}
      {(stats?.autoNavUnlocked || (stats?.droneMax ?? 0) > 0) && (
        <div style={{ position: "absolute", bottom: 6, left: "50%", transform: "translateX(-50%)", display: "flex", alignItems: "center", gap: 6, background: "rgba(0,0,0,0.65)", padding: "4px 8px", borderRadius: 20, border: "1px solid rgba(255,255,255,0.1)", zIndex: 2 }}>
          {stats?.autoNavUnlocked && (
            <button
              onClick={() => setAutoNav(v => !v)}
              style={{ background: autoNav ? "rgba(0,220,150,0.15)" : "rgba(255,255,255,0.04)", border: `1px solid ${autoNav ? "rgba(0,220,150,0.45)" : "rgba(255,255,255,0.15)"}`, borderRadius: 12, padding: "3px 10px", color: autoNav ? "#33ffaa" : "rgba(255,255,255,0.5)", fontFamily: "inherit", fontSize: 11, fontWeight: 700, cursor: "pointer", letterSpacing: 0.5 }}>
              🤖 AUTO {autoNav ? "ON" : "OFF"}
            </button>
          )}
          {(stats?.droneMax ?? 0) > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <button onClick={() => setDroneCount(v => Math.max(0, v - 1))} style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, width: 22, height: 22, color: "rgba(255,255,255,0.6)", fontFamily: "inherit", fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>−</button>
              <span style={{ fontSize: 11, fontWeight: 700, color: "#ffcc44", minWidth: 60, textAlign: "center" }}>🛸 {droneCount}/{stats.droneMax}</span>
              <button onClick={() => setDroneCount(v => Math.min(stats?.droneMax ?? 0, v + 1))} style={{ background: "rgba(255,200,50,0.08)", border: "1px solid rgba(255,200,50,0.3)", borderRadius: 8, width: 22, height: 22, color: "#ffcc44", fontFamily: "inherit", fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>+</button>
            </div>
          )}
        </div>
      )}

      {/* Node perk indicators */}
      {nodeActivePerks.length > 0 && (
        <div style={{ position: "absolute", bottom: (stats?.autoNavUnlocked || (stats?.droneMax ?? 0) > 0) ? 36 : 6, left: "50%", transform: "translateX(-50%)", display: "flex", gap: 4, background: "rgba(0,0,0,0.5)", padding: "4px 8px", borderRadius: 20, border: "1px solid rgba(255,255,255,0.06)", zIndex: 2, pointerEvents: "none" }}>
          {nodeActivePerks.map((pk, i) => <span key={i} title={`${pk.name}: ${pk.desc}`} style={{ fontSize: 13 }}>{pk.icon}</span>)}
        </div>
      )}

      {/* Node drop pool panel — what can spawn here */}
      {nodeDropPool && nodeDropPool.length > 0 && (
        <div style={{ position: "absolute", bottom: isDead ? 6 : 30, right: 6, background: "rgba(0,0,0,0.6)", borderRadius: 6, padding: "6px 8px", backdropFilter: "blur(6px)", border: "1px solid rgba(255,255,255,0.08)", zIndex: 2, pointerEvents: "none" }}>
          <div style={{ fontSize: 10, letterSpacing: 1, color: "rgba(120,180,240,0.85)", marginBottom: 4, fontWeight: 700 }}>NODE DROPS</div>
          {nodeDropPool.map(id => {
            const di = DEBRIS_ITEMS.find(d => d.id === id);
            const isDisc = di?.category === "discoverable";
            return di ? (
              <div key={id} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, padding: "2px 0" }}>
                <span>{di.icon}</span>
                <span style={{ color: isDisc ? "#dd88ff" : di.color, fontWeight: isDisc ? 700 : 400 }}>{di.name}</span>
              </div>
            ) : null;
          })}
          {nodeRefinedPool && nodeRefinedPool.length > 0 && (
            <div style={{ marginTop: 4, paddingTop: 4, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
              {nodeRefinedPool.map(id => {
                const bm = BASE_MATERIALS.find(m => m.id === id);
                return bm ? (
                  <div key={id} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, padding: "2px 0", opacity: 0.65 }}>
                    <span>{bm.icon}</span>
                    <span style={{ color: bm.color }}>~8% {bm.name}</span>
                  </div>
                ) : null;
              })}
            </div>
          )}
        </div>
      )}

    </div>
  );
}
